import { VideoHandlerBase, VideoHandlerConfig } from './Abstract';
import Vimeo from '@vimeo/player';
import { Log } from '../../AuxillaryService/Log';
import { Media } from './types';

export type VimeoHandlerConfig = VideoHandlerConfig & {
    vimeoVideoId: number | string;
    showSubtitles?: boolean;
};

export class VimeoHandler extends VideoHandlerBase<Vimeo> {
    protected _type: string;
    protected _videoId: number | string;
    protected _showSubtitles: boolean;

    constructor(config: VimeoHandlerConfig) {
        super(config);
        this._videoId = config.vimeoVideoId;
        this._type = 'vimeo';
        this._showSubtitles = config.showSubtitles || false;
    }

    protected async _createNewPlayer(): Promise<Vimeo> {
        const element = this.getElement();

        const options = {
            url: `https://player.vimeo.com/video/${this._videoId}`,
            width: element?.offsetWidth,
            height: element?.offsetHeight,
            loop: false,
            muted: this._muted,
            autopause: true,
            controls: this._showControls,
            title: false,
        };

        const player = new Vimeo(element, options);

        player.on('play', (data) => {
            Log.Info(`[${this.id}] Video playback started`, null, __fileline);
            Log.Debug(`[${this.id}] vimeo-play`, null, __fileline, data);
        });

        player.on('ended', (data) => {
            Log.Info(`[${this.id}] Video playback ended`, null, __fileline);
            Log.Debug(`[${this.id}] vimeo-ended`, null, __fileline, data);
            this.eventEmitter.emitEvent(Media.Ended);
        });

        player.on('error', (err) => {
            Log.Error(`[${this.id}] ${err.method} - ${err.name}: ${err.message}`, null, __fileline);
        });

        player.on('loaded', (data) => {
            Log.Debug(`[${this.id}] vimeo-loaded`, null, __fileline, data);
        });

        player.on('bufferstart', (data) => {
            Log.Debug(`[${this.id}] vimeo-bufferstart`, null, __fileline, data);
        });

        player.on('bufferend', (data) => {
            Log.Debug(`[${this.id}] vimeo-bufferend`, null, __fileline, data);
        });

        player.on('pause', (data) => {
            Log.Debug(`[${this.id}] vimeo-paused`, null, __fileline, data);
        });

        player.on('seeked', (data) => {
            Log.Debug(`[${this.id}] vimeo-seeked`, null, __fileline, data);
        });

        player.on('volumechange', (data) => {
            Log.Debug(`[${this.id}] vimeo-volumechange`, null, __fileline, data);
        });

        await player.ready();

        if (this._muted) {
            await player.setMuted(true);
        }

        if (this._showSubtitles) {
            try {
                await player.enableTextTrack('en');
            } catch (error) {
                const errName = error instanceof Error ? error.name : 'n/a';
                switch (errName) {
                    case 'InvalidTrackLanguageError':
                    case 'InvalidTrackError':
                        Log.Error(
                            `Vimeo video failed to enable closed captioning: ${String(error)}`,
                            null,
                            __fileline
                        );
                        break;
                    default:
                        throw error;
                }
            }
        }

        return player;
    }

    protected async _setCurrentTime(seconds: number): Promise<void> {
        const player = this._getAttachedPlayer();
        player.setCurrentTime(seconds);
    }

    protected async _doPlay(): Promise<void> {
        const player = this._getAttachedPlayer();
        await player.play();
    }

    protected async _doPause(): Promise<void> {
        const player = this._getAttachedPlayer();
        await player.pause();
    }

    protected async _doDestroyPlayer(): Promise<void> {
        const player = this._getPossiblyDetachedPlayer();
        await player.destroy();
    }
}
